import type { SplitDevice, SplitIntoRows } from "./types";

type Axis = "x" | "y";
const other = (axis: Axis): Axis => (axis === "x" ? "y" : "x");

const median = (values: number[]): number => {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
};

const nearestGap = (device: SplitDevice, devices: SplitDevice[]): number =>
  devices.reduce(
    (best, one) =>
      one.id === device.id
        ? best
        : Math.min(best, Math.hypot(one.position.x - device.position.x, one.position.y - device.position.y)),
    Number.POSITIVE_INFINITY,
  );

// Devices sharing one coordinate (within tolerance) are one row: a light row shares its x or its y.
const bandsAlong = (devices: SplitDevice[], axis: Axis, tolerance: number): SplitDevice[][] => {
  const sorted = [...devices].sort((a, b) => a.position[axis] - b.position[axis]);
  const bands: SplitDevice[][] = [];
  sorted.forEach((device) => {
    const band = bands[bands.length - 1];
    if (band && device.position[axis] - band[band.length - 1].position[axis] <= tolerance) band.push(device);
    else bands.push([device]);
  });
  return bands;
};

const chunk = (ids: string[], size: number): string[][] => {
  const parts = Math.ceil(ids.length / size);
  const even = Math.ceil(ids.length / parts);
  return Array.from({ length: parts }, (_, index) => ids.slice(index * even, (index + 1) * even));
};

export const splitIntoRows: SplitIntoRows = ({ devices, maxPerGroup }) => {
  const max = Math.max(1, Math.floor(maxPerGroup));
  if (devices.length <= max) return devices.length ? [devices.map((device) => device.id)] : [];

  const tolerance = Math.max(1, median(devices.map((device) => nearestGap(device, devices))) * 0.5);
  const byX = bandsAlong(devices, "x", tolerance);
  const byY = bandsAlong(devices, "y", tolerance);
  const axis: Axis = byX.length <= byY.length ? "x" : "y";
  const bands = axis === "x" ? byX : byY;

  // Snake through the rows, so one group's last device sits next to the next group's first.
  const rows = bands.map((band, index) => {
    const along = [...band].sort((a, b) => a.position[other(axis)] - b.position[other(axis)]);
    return (index % 2 ? along.reverse() : along).map((device) => device.id);
  });

  // Whole rows are packed together while they fit; only a row longer than the limit is cut.
  const groups: string[][] = [];
  let current: string[] = [];
  rows.forEach((row) => {
    if (row.length > max) {
      if (current.length) groups.push(current);
      current = [];
      groups.push(...chunk(row, max));
      return;
    }
    if (current.length + row.length > max) {
      groups.push(current);
      current = [];
    }
    current = [...current, ...row];
  });
  if (current.length) groups.push(current);
  return groups;
};
